import React, { useEffect, useState } from "react";
import shortUUID from "short-uuid";
import Select from "react-select";
import { PlusIcon } from "../../assets/Icons";
import apiClient from "../../apiClient";
import { SSO_URL } from "../../env";
import {
  FETCH_APPLICATION_TEMPLATE,
  SAVE_DEFAULT_CONFIGURATION,
} from "../../apiClient/apiEndpoints";
import { useParams } from "react-router-dom";
import { getValueType } from "../../utils/general";
import { debounce } from "../common/Debounce";

const generateShortUUID = () => shortUUID.generate();

function parseValue(value, dataType) {
  switch (dataType) {
    case 'number':
      return Number(value);
    case 'boolean':
      return value === 'true' ? true : value === 'false' ? false : value;
    case 'object':
      if (typeof value === 'string') return JSON.parse(value);
      return value;
    case 'string':
      return value;
    default:
      return value;
  }
}

function validateValue(value, dataType) {
  try {
    switch (dataType) {
      case 'number':
        return !isNaN(Number(value));
      case 'boolean':
        return value === 'true' || value === 'false' || typeof value === 'boolean';
      case 'object':
        if (typeof value === 'object') return true;
        if (typeof value === 'string') {
          const parsed = JSON.parse(value);
          return typeof parsed === 'object' && parsed !== null;
        }
        return false;
      case 'string':
        return typeof value === 'string';
      default:
        return false;
    }
  } catch (err) {
    return false;
  }
}


const dataTypeOptions = [
  {
    label: "String",
    value: "string",
  },
  {
    label: "Number",
    value: "number",
  },
  {
    label: "Boolean",
    value: "boolean",
  },
  {
    label: "Object",
    value: "object",
  },
];


const UpdateDefaultConfiguration = () => {
  const { appId, id } = useParams();
  const [dataList, setDataList] = useState([]);
  const [formError, setFormError] = useState(false);
  const [loading, setLoading] = useState(false);


  const getDefaultTemplate = async () => {
    try {
      const response = await apiClient.get(`${SSO_URL}${FETCH_APPLICATION_TEMPLATE}/${id}`);
      const configDataVariables = response?.data || {};
      const parsedList = Object.entries(configDataVariables).map(([key, value]) => ({
        name: key,
        value: getValueType(value) === "object" ? JSON.stringify(value || {}) : value,
        editable: false,
        key: generateShortUUID(),
        dataType: getValueType(value),
      }));
      setDataList(parsedList);
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddClick = () => {
    setDataList([...dataList, { name: "", value: "", key: generateShortUUID(), editable: true, dataType: "string" }]);
  };

  const handleRemoveClick = (key) => {
    setDataList((prev) => prev.filter((item) => item.key !== key));
  };

  const handleChange = (key, field, value) => {
    setDataList((prev) =>
      prev.map((item) => {
        if (item.key === key) {
          return {
            ...item,
            [field]: value,
          };
        } else {
          return item;
        }
      })
    );
  };

   const handleSaveAppConfiguration = async () => {
    if(loading) return;
    try {
      setLoading(true);
      const payload = {
        data: {}
      };

      for (const item of dataList) {
        if (validateValue(item.value, item.dataType)) {
          const parsedValue = parseValue(item.value, item.dataType);
            payload.data[item.name] = parsedValue;
        } else {
          setFormError(true);
          return;
        }
      }

      setFormError(false);

       await apiClient.post(`${SSO_URL}${SAVE_DEFAULT_CONFIGURATION}/${appId}/${id}`, payload);
       getDefaultTemplate();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
      getDefaultTemplate();
  }, [id]);

  const debounceHandleSave = debounce(handleSaveAppConfiguration, 500);

  return (
    <>
      <div className="variable-list ">

        {dataList.length > 0  &&
          <div className="parent">
          <div>Configuration key</div>
          <div>Configuration value</div>
          <div>Configuration Type</div>
          <div>Action</div>
        </div>}

        {dataList.map((item, index) => (
            <div className="child" key={index}>
              <div className="standardinput">
                <input
                  type="text"
                  disabled={!item.editable}
                  value={item.name}
                  className="input form-control"
                  onChange={(e) => handleChange(item.key, "name", (e.target.value).replace(/\s/g, ''))}
                />
              </div>
              <div className="standardinput">
                <input
                  type="text"
                  disabled={!item.editable}
                  value={item.value}
                  className="input form-control"
                  onChange={(e) => handleChange(item.key, "value", e.target.value)}
                />
              </div>
              <div className="standardinput">
                <div className="forselect-input">
                  <Select
                    theme={(theme) => ({
                      ...theme,
                      borderRadius: 5,
                      colors: {
                        ...theme.colors,
                        primary50: "#ca373c50",
                        primary25: "#ca373c50",
                        primary: "#ca373c70",
                        danger: "#000000",
                        neutral30: "#00000050",
                      },
                    })}
                    isDisabled={!item.editable}
                    value={dataTypeOptions.find((type) => type.value === item.dataType)}
                    options={dataTypeOptions}
                    onChange={(option) => handleChange(item.key, "dataType", option.value)}
                  />
                </div>
              </div>
              <div className="edit-remove">
                <button onClick={() => handleChange(item.key, "editable", item.editable ? false : true)}>
                    {item.editable ? "Lock" : "Edit" }
                  </button>

                  <button className="remove" onClick={() => handleRemoveClick(item.key)}>
                    Remove
                  </button>

              </div>
            </div>
          ))}

        <div className="add-new mt-3 mb-5">
            <button className="DefaultRedBtn" onClick={handleAddClick}>
              <PlusIcon />
              Add Variable
            </button>
        </div>
        {formError && <p className="error">Data and Data type are not matching</p>}
        <div className="footerbtn w-100 mt-5">
          <button className="cancel"> Cancel </button>
          {!loading ? (
            <button className="save" onClick={debounceHandleSave}>
              Save
            </button>
          ) : (
            <button className="loading save">
              {" "}
              <svg viewBox="25 25 50 50">
                {" "}
                <circle r="20" cy="50" cx="50"></circle>{" "}
              </svg>{" "}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default UpdateDefaultConfiguration;